import { AISingleEventAnalysis, EventHistoryDashboard, EventItem, TicketSource } from '../../../models/event';
import { SeatRecommendation, TopOpportunity } from '../../../models/seat';
import { PreFilteringRule } from '../../SearchEvents';
import { BulkAnalysisResult } from '../BulkAnalysisTab';

/**
 * Extended DataProvider interface with custom postData method
 */
export interface ExtendedDataProvider {
    postData: <T = unknown>(resource: string, action: string, data: unknown, method?: string) => Promise<{ data: T }>;
    fetchData: <T = unknown>(resource: string, query: string) => Promise<{ data: T }>;
    currentUser: <T = unknown>() => Promise<T>;
}

export interface MarketVelocityFeatures {
    [key: string]: any;
}

export interface MLData {
    topLots?: TopOpportunity[];
    summaryDetails: {
        recommendationCounts?: {
            strongBuy?: number;
            buy?: number;
            hold?: number;
            avoid?: number;
        }
    },
    eventQualityScore?: number;
    totalLotsScored?: number;
}
/**
 * Structure for AI scoring data
 */
export class AIScoring {
    public analysisTimestamp?: string;;
    public eventId?: bigint;
    public overallEventScore?: number;
    public recommendationLevel?: string;
    public profitabilityPrediction?: number;
    public riskAssessment?: number;
    public positiveFactors?: string[];
    public riskFactors?: string[];
    public eventName?: string;
    public siteId?: string;
    public haveSeatAnalysis?: boolean;
    public scores?: {
        overallScore: number;
        profitabilityPrediction: number;
        riskAssessment: number;
        totalPositiveScore: number;
        totalRiskScore: number;
    };
    public recommendation?: {
        decision: string;
        confidenceLevel?: string;
    };
    public factors?: {
        positiveFactors?: string[];
        riskFactors?: string[];
    };
    public analysisId?: number;
    public featuresJson?: string | null;
    public venueId?: number | null;
    public performerId?: number | null;
    public performerName?: string | null;
    public prepareData?: PrepareData | null;
    public velocityData?: MarketVelocityFeatures | null;
    public mlData?: MLData | null;
    private constructor (init?: Partial<AIScoring>) {
        Object.assign(this, init);
    }

    public static fromSingleEventAnalysis (data: AISingleEventAnalysis): AIScoring {
        let parsedPositiveFactors: string[] = [];
        let parsedRiskFactors: string[] = [];

        try {
            // Convert JSON string of PositiveFactors to array
            if (data.positiveFactors) {
                parsedPositiveFactors = JSON.parse(data.positiveFactors);
            }
        } catch (e) {
            console.error('Failed to parse positiveFactors JSON:', e);
        }

        try {
            // Convert JSON string of RiskFactors to array
            if (data.riskFactors) {
                parsedRiskFactors = JSON.parse(data.riskFactors);
            }
        } catch (e) {
            console.error('Failed to parse riskFactors JSON:', e);
        }

        return new AIScoring({
            eventId: data.eventId,
            eventName: data.eventName,
            siteId: data.siteId,
            analysisId: data.id,
            overallEventScore: data.overallScore ?? undefined,
            recommendationLevel: data.recommendationDecision ?? undefined,
            riskAssessment: data.totalRiskScore ?? undefined,
            haveSeatAnalysis: data.haveSeatAnalysis,
            
            positiveFactors: parsedPositiveFactors,
            riskFactors: parsedRiskFactors,
            factors: {
                positiveFactors: parsedPositiveFactors,
                riskFactors: parsedRiskFactors,
            },
            
            scores: {
                overallScore: data.overallScore ?? 0,
                totalPositiveScore: data.totalPositiveScore ?? 0,
                totalRiskScore: data.totalRiskScore ?? 0,
                profitabilityPrediction: 0, // Not available in the source interface
                riskAssessment: data.totalRiskScore ?? 0,
            },

            recommendation: {
                decision: data.recommendationDecision ?? 'N/A',
                confidenceLevel: data.recommendationConfidence ?? undefined,
            },
            featuresJson: data.featuresJson,
            venueId: data.venueId,
            performerId: data.performerId,
            performerName: data.performerName
        });
    }
    public static fromBulkAnalysis (data: BulkAnalysisResult): AIScoring {
        return new AIScoring({
            analysisTimestamp: data.analysisTimestamp,
            eventId: data.eventId,
            siteId: data.siteId,
            eventName: data.eventName,
            
            // Mapping Factors
            positiveFactors: data.factors?.positiveFactors || [],
            riskFactors: data.factors?.riskFactors || [],
            factors: data.factors,

            // Mapping Recommendation
            recommendation: {
                decision: data.recommendation.decision,
                confidenceLevel: data.recommendation.confidenceLevel,
            },
            recommendationLevel: data.recommendation.decision,

            // Mapping Scores (Xử lý các trường không khớp)
            scores: {
                overallScore: data.scores.overallScore || 0,
                totalPositiveScore: data.scores.totalPositiveScore || 0,
                totalRiskScore: data.scores.totalRiskScore || 0,
                // AIScoring yêu cầu 2 trường này nhưng Bulk không có -> Bù đắp giá trị
                profitabilityPrediction: 0,
                riskAssessment: data.scores.totalRiskScore || 0,
            },
            overallEventScore: data.scores.overallScore,
            riskAssessment: data.scores.totalRiskScore,

            // Mapping Velocity
            velocityData: data.velocitySnapshot,
            
            // Các trường mặc định khác
            haveSeatAnalysis: false,
            mlData: null
        });
    }
}

/**
 * Structure for prepare data from API
 */
export interface PrepareData {
    id: string;
    siteId?: string;
    eventId?: bigint;
    performerPopularityScore?: number;
    isSingleShowAtVenue?: boolean;
    venueLayoutType?: string;
    isPostponed?: boolean;
    primaryPriceLevelChange?: number;
    artistHasSpecSalesHistory?: boolean;
    artistHasRealSalesHistory?: boolean;
    isVenueDifficultToBuy?: boolean;
    isEasyOfPurchase?: boolean;
    isArtistInExpiredInventory?: boolean;
    hasSeatMap?: string;
    isSeasonalEvent?: boolean;
    deliveryMethod?: number | null;
    delayDays?: number | null;
    // Event details
    onsaleDate?: string;
    marketType?: string;
    eventCategoryName?: string;
    event_day_of_week?: string;
    event_time?: number;
    eventDate?: string;
    venueId?: number | null;
    venueName?: string;
    performerId?: number | null;
    venueCapacity?: number;
    time_left_days?: number;
    event_timing_factors?: string;
    primarySalesVelocity30d?: number;
    primaryTicketSalesPerformance?: string;
    primaryPercentLeft?: number;
    primaryCurrentAvailabilityCount?: number;
    tnTotalSales?: number;
    secondariesCompetitionCount?: number;
    secondariesPriceTrend?: string;
    liveTickets: any[];
    haveSeatAnalysis?: boolean;
    event?: {
        id: bigint;
        url: string;
        type: string;
        dateTime: string;
        venue?: string;
        city?: string;
        state?: string;
        country?: string;
        name?: string;
        siteId?: string;
    };

    flattenedHistory?: {
        flattenedData?: {
            primary?: TicketSource | null,
            ticketNetwork?: TicketSource | null,
            vivid?: TicketSource | null,
            goTickets?: TicketSource | null
        };
    };
}

export interface PrepareDataResponse {
    eventData: PrepareData;
    message?: string;
}

/**
 * Structure for history sale state
 */
export interface HistorySaleState {
    open: boolean;
    loading: boolean;
    error: string | null;
    data: any[];
}

/**
 * Component props for SingleEventAnalysisTab
 */
export interface SingleEventAnalysisTabProps {
    selectedEvent: EventItem | null;
    setSelectedEvent: (event: EventItem | null) => void;
    analysisCompleted: boolean;
    aiScoring: AIScoring | null;
    seatRecommendations: SeatRecommendation[];
    setAiScoring: (data: AIScoring | null) => void;
    setSeatRecommendations: (data: SeatRecommendation[]) => void;
    preFilteringRules?: PreFilteringRule[];
    setSearchResults: (data: any) => void;
}

/**
 * Ref handle methods exposed to parent component
 */
export interface SingleEventAnalysisTabHandle {
    fetchScoringData: (event: EventItem) => Promise<AIScoring>;
    fetchHistoricalScoringData: (analysisId: number | bigint, target?: EventHistoryDashboard) => Promise<AIScoring>;
    fetchSeatRecommendations: (event: EventItem | null, analysisId: number) => Promise<SeatRecommendation[]>;
    getSeatRecommendationsHistory: (analysisId: number | bigint) => Promise<SeatRecommendation[]>;
}

/**
 * Props for EventInfoCard component
 */
export interface EventInfoCardProps {
    aiScoring: AIScoring | null;
    selectedEvent: EventItem;
    setSelectedEvent: (event: EventItem | null) => void;
    analysisCompleted: boolean;
    onVenueMapClick: (venueId: number, venueName?: string) => void;
    setAiScoring: (data: AIScoring | null) => void;
    setSeatRecommendations: (data: SeatRecommendation[]) => void;
    getAnalysisEventScore: (event: EventItem) => Promise<AIScoring>;
    getSeatRecommendations: (event: EventItem, analysisId: number) => Promise<SeatRecommendation[]>;
}

/**
 * Props for ScoringSection component
 */
export interface AIScoringSectionProps {
    selectedEvent: EventItem;
    aiScoring: AIScoring | null;
    setAiScoring: (data: AIScoring | null) => void;
    getRecommendationIcon: (level: string, props?: any) => React.ReactNode;
    getScoreColor: (score: number) => 'success' | 'warning' | 'error' | 'default' | 'primary' | 'secondary' | 'info';
}

/**
 * Props for HistorySection component
 */
export interface HistorySectionProps {
    selectedEvent: EventItem;
}

/**
 * Props for SeatRecommendationComponent
 */
export interface SeatRecommendationComponentProps {
    analysisCompleted: boolean;
    seatRecommendations: SeatRecommendation[];
    isSeatRecommendationsLoading: boolean;
    preFilteringRules?: PreFilteringRule[];
    setSeatRecommendations: (data: SeatRecommendation[]) => void;
    selectedEvent?: EventItem | null;
    aiScoring?: AIScoring | null;
    setAiScoring: (data: AIScoring | null) => void;
    selectEvent?: EventItem | null;
    setSearchResults: (data: any) => void;
}

export function calculateComponentScores (features: MarketVelocityFeatures): {
    primaryScore: number;
    eventScore: number;
    venueScore: number;
    daysScore: number;
} {
    // Component 1: Primary Sales Velocity (days_before_event_sells_out)
    let primaryScore = 0;
    const daysBefore = features.days_before_event_sells_out;
    if (daysBefore <= -30) {
        primaryScore = -40;
    } else if (daysBefore <= 0) {
        primaryScore = -10;
    } else if (daysBefore <= 15) {
        primaryScore = 5;
    } else if (daysBefore <= 30) {
        primaryScore = 15;
    } else if (daysBefore <= 40) {
        primaryScore = 30;
    } else {
        primaryScore = 40;
    }

    // Component 2: Event Total Sales (tn_all_time_number_of_sales + total_spec_sales_event)
    const eventSales = features.tn_all_time_number_of_sales + (features.total_spec_sales_event ?? 0);
    let eventScore = 0;
    if (eventSales === 0) {
        eventScore = -20;
    } else if (eventSales <= 3) {
        eventScore = 0;
    } else if (eventSales <= 8) {
        eventScore = 10;
    } else if (eventSales <= 20) {
        eventScore = 20;
    } else {
        eventScore = 30;
    }

    // Component 3: Venue Total Sales (venue_total_sales)
    let venueScore = 0;
    const venueSales = features.venue_total_sales;
    if (venueSales === 0) {
        venueScore = -20;
    } else if (venueSales <= 50) {
        venueScore = 0;
    } else if (venueSales <= 100) {
        venueScore = 5;
    } else if (venueSales <= 200) {
        venueScore = 10;
    } else if (venueSales <= 400) {
        venueScore = 15;
    } else {
        venueScore = 20;
    }

    // Component 4: Days Until Event (days_until_event)
    let daysScore = 0;
    const daysUntil = features.days_until_event;
    if (daysUntil < 15) {
        daysScore = -50;
    } else if (daysUntil <= 30) {
        daysScore = -10;
    } else if (daysUntil <= 60) {
        daysScore = 5;
    } else {
        daysScore = 10;
    }

    return { primaryScore, eventScore, venueScore, daysScore };
}

export function calculateSimplifiedScore (features: MarketVelocityFeatures): number {
    const components = calculateComponentScores(features);
    const simplifiedScore = Object.values(components).reduce((sum, val) => sum + val, 0);
    return simplifiedScore;
}

export function MappingComponentScores (features: MarketVelocityFeatures | null): MarketVelocityFeatures | null {
    if (!features) return null;

    const calculatedScores = calculateComponentScores(features);
    // mapping scores to number if they are objects
    // Primary tooltip with all scoring rules
    
    const newVelocityFeatures: any = { ...features };
    if ( !newVelocityFeatures.simplifiedScore) {
        newVelocityFeatures.simplifiedScore = calculateSimplifiedScore(features);
    }
    
    Object.entries(calculatedScores).forEach(([key, scoreValue]) => {
        // Find corresponding config from COMPONENT_SCORE_METADATA
        const meta = COMPONENT_SCORE_METADATA[key];

        if (meta) {
            // If metadata exists, map score with text and tooltip
            newVelocityFeatures[key] = {
                score: scoreValue,
                text: meta.text,
                tooltip: meta.tooltip
            };
        } else {
            // (Optional) Handle case where a newly calculated score is missing Config
            // Can assign default or log warning
            console.warn(`Missing metadata config for score key: ${key}`);
            newVelocityFeatures[key] = {
                score: scoreValue,
                text: key, // fallback to key as text
                tooltip: ''
            };
        }
    });

    return newVelocityFeatures;
}
const COMPONENT_SCORE_METADATA: Record<string, { text: string; tooltip: string }> = {
    primaryScore: {
        text: 'Days until Event - Primary Days to Sellout',
        tooltip: `Days before event sells out:
                • ≤ -30     → Score = -40
                • -30 to 0  → Score = -10
                • 1-15      → Score = +5
                • 16-30     → Score = +15
                • 31-40     → Score = +30
                • ≥ 41      → Score = +40`
    },
    eventScore: {
        text: 'TN Sales for Event + Song Spec Sales for Event',
        tooltip: `Total sales (spec + tn sales):
                • 0       → Score = -20
                • 1-3     → Score = 0
                • 4-8     → Score = +10
                • 9-20    → Score = +20
                • ≥ 21    → Score = +30`
    },
    venueScore: {
        text: 'Venue Total Sales (Spec + TN total)',
        tooltip: `Venue total sales:
                • 0       → Score = -20
                • 1-50    → Score = 0
                • 51-100  → Score = +5
                • 101-200 → Score = +10
                • 201-400 → Score = +15
                • ≥ 401   → Score = +20`
    },
    daysScore: {
        text: 'Days Until Event',
        tooltip: `Days until event:
                • < 15     → Score = -50
                • 15-30    → Score = -10
                • 31-60    → Score = +5
                • > 60     → Score = +10`
    }
};